import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { AlertTriangle, Trash2, Cloud, HardDrive, Image as ImageIcon, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { usage, fmtBytes, clearMediaOnly, clearMediaOlderThan, clearConversationCache, clearOfflineData, saveOfflineSettings, readConversations, syncNow, subscribeOffline } from "../lib/offlineSync";

/** Free-up screen: delete media / conversation caches (also used from Settings). */
export const OfflineManagePanel = ({ open, onClose, user, t, onOnlineOnly }) => {
  const [use, setUse] = useState(null);
  const [convs, setConvs] = useState([]);
  const refresh = () => { usage().then(setUse).catch(() => {}); readConversations().then((l) => setConvs(l.filter((c) => c.type !== "buddy"))); };
  useEffect(() => { if (open) refresh(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => subscribeOffline((type) => { if (type === "media" || type === "state") refresh(); }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, msg) => { await fn(); toast.success(msg); refresh(); };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="offline-manage-panel">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><HardDrive className="h-5 w-5 text-primary" />{t("offline_manage_title")}</DialogTitle>
        </DialogHeader>
        {use && (
          <div className="text-xs text-muted-foreground" data-testid="offline-manage-usage">
            {use.mediaCount} {t("offline_media_files")} · {fmtBytes(use.mediaBytes)} · {use.messages} {t("msgs").toLowerCase()}
            {use.quota?.quota ? ` · ${t("offline_device_free")}: ${fmtBytes(use.quota.quota - use.quota.usage)}` : ""}
          </div>
        )}
        <div className="grid gap-2">
          <Button variant="outline" className="justify-start" data-testid="offline-clear-media-30" onClick={() => act(() => clearMediaOlderThan(30), t("done"))}>
            <ImageIcon className="h-4 w-4 mr-2" />{t("offline_clear_media_30")}
          </Button>
          <Button variant="outline" className="justify-start" data-testid="offline-clear-media-all" onClick={() => act(clearMediaOnly, t("done"))}>
            <Trash2 className="h-4 w-4 mr-2" />{t("offline_clear_media_all")}
          </Button>
        </div>
        {convs.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{t("offline_clear_conv_title")}</div>
            <div className="max-h-48 overflow-y-auto rounded-xl border divide-y">
              {convs.map((c) => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate flex-1">{c.name || c.other_user?.name || c.id}</span>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid={`offline-clear-conv-${c.id}`} onClick={() => act(() => clearConversationCache(c.id), t("done"))}>{t("offline_clear")}</Button>
                </div>
              ))}
            </div>
          </div>
        )}
        {onOnlineOnly && (
          <Button variant="secondary" className="w-full" data-testid="offline-go-online-only" onClick={onOnlineOnly}><Cloud className="h-4 w-4 mr-2" />{t("offline_online_only_btn")}</Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Modal shown when the device storage is full during sync. */
export const OfflineFullDialog = ({ open, onClose, user, t }) => {
  const [manage, setManage] = useState(false);
  const onlineOnly = async () => {
    saveOfflineSettings(user.id, { enabled: false });
    await clearOfflineData(user.id);
    toast.info(t("offline_disabled_toast"));
    onClose();
  };
  return (
    <>
    <OfflineManagePanel open={open && manage} onClose={() => { setManage(false); onClose(); syncNow(user); }} user={user} t={t} onOnlineOnly={onlineOnly} />
    <Dialog open={open && !manage} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" data-testid="offline-full-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" />{t("offline_full_title")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t("offline_full_body")}</p>
        <div className="flex flex-col gap-2 pt-1">
          <Button data-testid="offline-full-manage" onClick={() => setManage(true)}><Trash2 className="h-4 w-4 mr-2" />{t("offline_full_manage_btn")}</Button>
          <Button variant="outline" data-testid="offline-full-online-only" onClick={onlineOnly}><Cloud className="h-4 w-4 mr-2" />{t("offline_online_only_btn")}</Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
